/** @jsx isolateComponent */

import { Fragment, useState } from 'react';

import { FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Button, Heading, Spacer, Text } from '@zapier/design-system';
import { useSendFullStoryEvent } from '@zapier/toolbox-tracking';

import { TrialType } from '@zapier/design-system-revenue/src/types';
import { useSize } from '@zapier/design-system-revenue/src/utils';

import { TrialOptionButton } from './TrialOptionButton';

type TrialTypeSelectorProps = {
  /** The function called when the continue button is clicked. */
  onSelect: (selectedTrialType: TrialType) => void;
};

/**
 * A wizard that guides visitors to the appropriate trial.
 */
export const TrialTypeSelector = ({ onSelect }: TrialTypeSelectorProps) => {
  useSendFullStoryEvent('Viewed Plan Recommender Modal');

  const size = useSize();
  const [selectedTrialType, setSelectedTrialType] = useState<TrialType>();

  return (
    <Fragment>
      <Heading>
        <Text type="header800">
          Save time and money by automating your work
        </Text>
      </Heading>

      <Spacer height={40} />

      <Text tag="p" type="subtitle900">
        Who will be using Zapier?{' '}
        <Text color="grey700" fontWeight={400} type="subtitle900">
          (chose one)
        </Text>
      </Text>

      <Spacer height={5} />

      <Text color="grey700" tag="p" type="altText900">
        Use Zapier alone or invite your whole crew with advanced multi-user
        features for teams and companies.
      </Text>

      <Spacer height={20} />

      <FlexLayout
        flex="no-flex"
        gutter="extraSmall"
        justify={size === 'small' ? 'center' : 'flex-start'}
      >
        <TrialOptionButton
          iconName="personOne"
          isSelected={selectedTrialType === 'Professional'}
          label="Just Me"
          onClick={() => setSelectedTrialType('Professional')}
        />

        <TrialOptionButton
          iconName="personGroup"
          isSelected={selectedTrialType === 'Team'}
          label="My Team"
          onClick={() => setSelectedTrialType('Team')}
        />

        <TrialOptionButton
          iconName="team"
          isSelected={selectedTrialType === 'Company'}
          label="My Company"
          onClick={() => setSelectedTrialType('Company')}
        />
      </FlexLayout>

      <Spacer height={40} />

      <FlexLayout
        direction={size === 'small' ? 'column' : 'row'}
        justify="flex-end"
      >
        <Button
          disabled={!selectedTrialType}
          hasShadow={true}
          isFullWidth={size === 'small'}
          onClick={() => onSelect(selectedTrialType as TrialType)}
        >
          Next
        </Button>
      </FlexLayout>
    </Fragment>
  );
};
